/**
 * Connection Status Store
 *
 * Tracks API connection state and retries to show user-friendly messages
 * when the server is starting up or experiencing issues.
 */
import { defineStore } from 'pinia'
import { ref, computed, onUnmounted } from 'vue'
import { connectionEvents } from '@/api'

export const useConnectionStore = defineStore('connection', () => {
  // State
  const isRetrying = ref(false)
  const retryCount = ref(0)
  const maxRetries = ref(8) // Cloud Run cold starts can take 30-60 seconds
  const retryReason = ref<'network' | 'server' | null>(null)
  const lastRetryTime = ref<Date | null>(null)
  const connectionEstablished = ref(false) // Once true, stay connected
  const lastSuccessTime = ref<Date | null>(null)

  // Computed
  const isConnecting = computed(() => isRetrying.value && !connectionEstablished.value)

  const connectionMessage = computed(() => {
    if (!isConnecting.value) return null

    if (retryReason.value === 'server') {
      if (retryCount.value === 1) {
        return 'Connecting to server...'
      }
      return 'Server is starting up, please wait...'
    }

    if (retryReason.value === 'network') {
      return 'Connection lost, reconnecting...'
    }

    return 'Connecting...'
  })

  // Actions
  function startRetry(reason: 'network' | 'server', count: number) {
    // If we've already established connection, don't show retry UI
    // (server might just be slow on one request)
    if (connectionEstablished.value) {
      return
    }

    // Only update count if it's higher (don't go backwards for parallel requests)
    if (count > retryCount.value || !isRetrying.value) {
      isRetrying.value = true
      retryCount.value = count
      retryReason.value = reason
      lastRetryTime.value = new Date()
    }
  }

  function endRetry(success: boolean) {
    if (success) {
      // Mark connection as established - won't show retry UI again this session
      connectionEstablished.value = true
      lastSuccessTime.value = new Date()
      isRetrying.value = false
      retryCount.value = 0
      retryReason.value = null
    }
    // Don't clear isRetrying on failure - keep showing UI until success
  }

  function reset() {
    isRetrying.value = false
    retryCount.value = 0
    retryReason.value = null
    lastRetryTime.value = null
    // Don't reset connectionEstablished - that persists
  }

  function fullReset() {
    reset()
    connectionEstablished.value = false
    lastSuccessTime.value = null
  }

  // Subscribe to API connection events
  let unsubscribeStart: (() => void) | null = null
  let unsubscribeEnd: (() => void) | null = null
  let unsubscribeConnected: (() => void) | null = null

  function init() {
    // Unsubscribe from previous listeners if any
    unsubscribeStart?.()
    unsubscribeEnd?.()
    unsubscribeConnected?.()

    unsubscribeStart = connectionEvents.on('retry-start', (payload) => {
      if (payload.reason && payload.count !== undefined) {
        startRetry(payload.reason, payload.count)
      }
    })

    unsubscribeEnd = connectionEvents.on('retry-end', (payload) => {
      endRetry(payload.success ?? false)
    })

    // Any successful response marks connection as established
    unsubscribeConnected = connectionEvents.on('connected', () => {
      if (!connectionEstablished.value) {
        connectionEstablished.value = true
        lastSuccessTime.value = new Date()
        isRetrying.value = false
        retryCount.value = 0
        retryReason.value = null
      }
    })
  }

  function cleanup() {
    unsubscribeStart?.()
    unsubscribeEnd?.()
    unsubscribeConnected?.()
  }

  // Auto-initialize
  init()

  return {
    // State
    isRetrying,
    retryCount,
    maxRetries,
    retryReason,
    lastRetryTime,
    connectionEstablished,
    lastSuccessTime,

    // Computed
    isConnecting,
    connectionMessage,

    // Actions
    startRetry,
    endRetry,
    reset,
    fullReset,
    init,
    cleanup,
  }
})
